import React, { useState, useEffect, ChangeEvent, useMemo } from 'react';

import Title from '@vromo/ui/components/Title';
import { ModalHeader, ModalContent, ModalActions } from '@vromo/ui/components/Modal';
import Button from '@vromo/ui/components/Button';
import Select from '@vromo/ui/components/Select';
import Select2 from '@vromo/ui/components/Select2';
import Input from '@vromo/ui/components/Input';

import { action, runInAction, when } from 'mobx';
import capitalize from 'lodash/capitalize';
import { useStore } from '@vromo/mobx-react';
import { observer, useLocalObservable } from 'mobx-react-lite';
import { update } from 'serializr';
import { useToasts } from '@vromo/ui/components/Toasts';
import { EntityList } from '@vromo/models';
import { Role } from '@vromo/models';
import { RootStore } from '../../stores/RootStore';
import { isValidEmail } from '@vromo/models/helpers';
import { UserAccessPosition } from '@vromo/api';
import { qaLabelTeams } from './../../../../../common-qa-labels/teams';
import sortBy from 'lodash/sortBy';
import { useTranslation } from 'react-i18next';
import styles from './form.style';

type NewDashboardUserFormProps = {
    roles: EntityList<Role>;
    close: () => void;
};

type NumberOption = {
    label: string;
    value: number;
};

export default observer(function NewDashboardUserForm({ roles, close }: NewDashboardUserFormProps) {
    const {
        groupStore,
        authStore: { role, graph, isFreemium }
    } = useStore<RootStore>('rootStore');

    const showToast = useToasts();
    const { t } = useTranslation();

    const store = useLocalObservable(() => ({
        get positions() {
            return {
                admin: isFreemium ? UserAccessPosition.freemiumAdmin : UserAccessPosition.admin,
                enterprise: UserAccessPosition.enterprise,
                user: isFreemium ? UserAccessPosition.freemiumUser : UserAccessPosition.user,
                client: UserAccessPosition.client
            } as { [key: string]: number };
        },
        get positionNames() {
            return ['Admin', 'Enterprise User', 'User', 'Client'];
        }
    }));

    const [group, setGroup] = useState(0);
    const [email, setEmail] = useState('');
    const [position, setPosition] = useState(store.positions.user);

    useEffect(() => {
        when(
            () => groupStore.loaded,
            action(() => {
                setGroup(groupStore.groups.first()!.id!);
            })
        );
    }, []);

    const groupOptions = sortBy(
        groupStore.groups.map((g) => ({ label: g.name, value: g.id })),
        ['label']
    );

    const emailsInGroup = useMemo(
        () =>
            roles
                .filter((item) => item.account?.id === group)
                .map((item) => item.user?.contact?.email?.toLowerCase().trim() || ''),
        [group, roles]
    );

    const isValidUser = () => {
        const allFieldsFilled = email.length !== 0;

        const validEmail = isValidEmail(email);

        const emailIsNotUsedInGroup = !emailsInGroup.includes(email.toLowerCase().trim());

        const showError = (validated: boolean, message: string) => {
            if (validated) {
                return true;
            } else {
                showToast(message);
                return false;
            }
        };

        return (
            showError(allFieldsFilled, t('teams.allFieldsFilledUser')) &&
            showError(validEmail, t('teams.invalidEmail')) &&
            showError(emailIsNotUsedInGroup, t('teams.userAlreadyExists'))
        );
    };

    async function inviteUser() {
        if (!group) {
            showToast(t('teams.noParentTeamSelected'), 'error');
            return;
        }

        if (!isValidUser()) return;

        const postData = new EntityList(Role);
        postData.push({
            position: { id: position! },
            user: { contact: { email: email } },
            account: { id: group }
        } as Role);

        const { items: accountRoles } = await graph.root
            .graph()
            .roles(role.id)
            .account()
            .roles()
            .post(postData);

        runInAction(() => {
            update(roles, { items: accountRoles, type: 'create' });
            close();
            showToast(t('teams.success'));
        });
    }

    return (
        <div>
            <ModalHeader>
                <Title data-testid={qaLabelTeams.newDashboardUserModal.title}>
                    {t('teams.inviteNewUser')}
                </Title>
            </ModalHeader>
            <ModalContent>
                <Input
                    label={t('teams.addNewUserEmail')}
                    placeholder={t('teams.emailPlaceholder')}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    data-testid={qaLabelTeams.newDashboardUserModal.emailInput}
                />
                <label className={styles.groupLabel}>{t('teams.userRole')}</label>
                <Select
                    onChange={(e: ChangeEvent<HTMLSelectElement>) =>
                        setPosition(parseInt(e.target.value))
                    }
                    data-testid={qaLabelTeams.newDashboardUserModal.roleDropdown}>
                    {Object.keys(store.positions).map((it, index) => (
                        <option
                            key={store.positions[it]}
                            value={store.positions[it]}
                            selected={position === store.positions[it]}>
                            {store.positionNames[index]}
                        </option>
                    ))}
                </Select>
                <label
                    data-testid={qaLabelTeams.newDashboardUserModal.groupLabel}
                    className={styles.groupLabel}>
                    {t('teams.userGroup')}
                </label>
                <Select2
                    data-testid={qaLabelTeams.newDashboardUserModal.groupDropdown}
                    className={styles.dropdown}
                    onChange={action((selected: NumberOption) => {
                        setGroup(selected?.value);
                    })}
                    value={groupOptions.find((g) => g.value === group) || null}
                    options={groupOptions}
                    placeholder={t('teams.selectGroup')}
                    isClearable
                />
            </ModalContent>

            <ModalActions>
                <Button
                    onClick={close}
                    data-testid={qaLabelTeams.newDashboardUserModal.cancelButton}>
                    {t('teams.cancel')}
                </Button>
                <Button
                    onClick={inviteUser}
                    data-testid={qaLabelTeams.newDashboardUserModal.saveButton}
                    primary>
                    {t('teams.save')}
                </Button>
            </ModalActions>
        </div>
    );
});
